import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    FeatureFlag,
    FolderType,
    IEnvironment,
    IPackage,
    IPackageArgumentCollection,
    IProcess,
    ModalResultType,
} from '@models';
import { MAX_NUMBER_OF_RESULTS } from '@pages/shared/constants';
import {
    EnvironmentService,
    ODataQueryOptions,
    PackageService,
    ProcessService,
} from '@services';
import { ISuggestValueData } from '@uipath/angular/components/ui-suggest';
import UiValidators, { UI_DESCRIPTION_MAX_LENGTH } from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    finalize,
    map,
    share,
    tap,
} from 'rxjs/operators';

import { ProcessSettingsComponent } from '../../shared';

@Component({
    templateUrl: './process.modal.html',
    styleUrls: ['./process.modal.scss'],
    providers: [
        FormBuilder,
        EnvironmentService,
        PackageService,
        ProcessService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class ProcessModalComponent
    extends UiModalComponent<IProcess>
    implements OnInit {

    public get selectedProcess() {
        return this.context.data.processKey ?
            {
                id: this.context.data.processKey,
                text: this.context.data.processKey,
            } :
            null;
    }

    public get isProcessSettingEnabled() {
        return this._appState.settings.isFeatureEnabled(FeatureFlag.MediaRecording) ||
            this._appState.settings.isFeatureEnabled(FeatureFlag.ProcessSettingAutoStart);
    }

    public isCurrentFolderClassic$!: Observable<boolean>;

    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);
    public args?: IPackageArgumentCollection;
    public packages$?: Observable<ISuggestValueData<IPackage>[]>;

    @ViewChild('processSetting', { static: false })
    public processSetting?: ProcessSettingsComponent;

    private _processService = this._injector.get<ProcessService>(ProcessService);
    private _environmentService = this._injector.get<EnvironmentService>(EnvironmentService);
    private _packageService = this._injector.get<PackageService>(PackageService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    ngOnInit() {
        this.isCurrentFolderClassic$ = this._appState.user.folder.isClassic$;

        this.form = this._injector.get<FormBuilder>(FormBuilder).group({
            'processKey': [
                [this.selectedProcess],
                UiValidators.required(),
            ],
            'processVersion': [
                [], (c: FormControl) => {
                    return this.selectedProcess == null ?
                        null :
                        UiValidators.required()(c);
                }],
            'description': [
                '',
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH)]
            ,
            'environment': [
                null,
                this._appState.user.folder.type === FolderType.Classic ? UiValidators.required() : [],
            ],
            'arguments': [
                {},
            ],
        });
    }

    public processSearchFactory = (query: string = '', top: number = MAX_NUMBER_OF_RESULTS, skip: number = 0) => {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['Id'],
            filter: {
                Id: {
                    contains: query,
                },
            },
            skip,
            top,
        };

        return this._packageService
            .get(queryOptions)
            .pipe(
                map(response => ({
                    data: this._mapProcesses(response.data),
                    total: response.total,
                })),
            );
    }

    public environmentSearchFactory = (query: string = '', step: number = MAX_NUMBER_OF_RESULTS, skip: number = 0) => {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['Name'],
            skip,
            filter: {
                Name: {
                    contains: query,
                },
            },
            top: step,
        };

        return this._environmentService
            .get(queryOptions)
            .pipe(
                map(response => ({
                    data: this._mapEnvironments(response.data),
                    total: response.total,
                })),
            );
    }

    public fetchPackages() {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['Version desc'],
        };

        this.packages$ = this._packageService
            .getVersions(this.context.data.processKey, queryOptions)
            .pipe(
                map(result =>
                    !result || !result.total ?
                        [] :
                        this._mapPackages(result.data),
                ),
                tap(packages => {
                    if (
                        packages &&
                        packages.length > 0
                    ) {
                        this.form.patchValue({ 'processVersion': [packages[0]] });
                        this.onVersionSelect(packages[0]);
                    } else {
                        // TODO: Refactor to prevent code duplication
                        this.form.patchValue({ 'processVersion': [] });
                        this.form.get('arguments')!.patchValue({});
                        this.args = undefined;
                    }
                }),
                share(),
            );
    }

    public onSubmit(_ev?: Event) {
        const isScreenRecordingInvalid = this.processSetting && this.processSetting.form.invalid;
        if (
            this.form.invalid ||
            isScreenRecordingInvalid ||
            this.loading$.value
        ) {
            return;
        }

        this.loading$.next(true);

        const payload = cloneDeep(this.context.data);
        payload.description = this.form.get('description')!.value;
        payload.name = this._appState.user.folder.type === FolderType.Classic
            ? `${this.context.data.processKey}_${this.context.data.environmentName}`
            : this.context.data.processKey;
        payload.inputArguments = this.form.get('arguments')!.value;

        if (
            this.isProcessSettingEnabled &&
            this.processSetting
        ) {
            payload.processSettings = this.processSetting.form.value;
        }

        this._processService.add(payload)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
            )
            .subscribe(
                () =>
                    this._dialogRef.close({
                        type: ModalResultType.Add,
                    }),
            );
    }

    public onVersionSelect({ data }: ISuggestValueData<IPackage>) {
        this.context.data.processVersion = data!.version;
        this.form.get('arguments')!.patchValue({});
        this.args = data!.arguments;
    }

    private _mapPackages = (packages: IPackage[]) => {
        return packages
            .map((pkg: IPackage) => {
                return {
                    id: pkg.key,
                    text: pkg.version,
                    data: pkg,
                };
            }) as ISuggestValueData<IPackage>[];
    }

    private _mapProcesses = (processes: IPackage[]) => {
        return processes.map((process: IPackage) => {
            return {
                id: process.id,
                text: process.id,
            };
        });
    }

    private _mapEnvironments = (environments: IEnvironment[]) => {
        return environments.map((environment: IEnvironment) => {
            return {
                id: environment.id,
                text: environment.name,
            };
        });
    }
}
